import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import {Subscription, finalize} from "rxjs";
import {
  CompletionDatesDto,
  SeriesCompletionDateDto,
  VolumeCompletionDateDto,
  GameCompletionDateDto,
  WorksModel,
  BookCompletionDateDto,
} from "src/app/models/WorksModel";
import {User} from "src/app/modules/auth/models/User";
import {WorksService} from "../../../shared/services/works.service";
import {ToastService} from "../../services/toast.service";
import {TranslateService} from "@ngx-translate/core";
import {WorkTimeHistoryComponent} from "../../../works/components/work-time-history/work-time-history.component";

@Component({
  selector: "app-base-edit-work-dates",
  templateUrl: "./base-edit-work-dates.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BaseEditWorkDatesComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() item!: WorksModel;
  @Input() user!: User;
  public submitText?: string;
  @ViewChild(WorkTimeHistoryComponent) timeHistory?: WorkTimeHistoryComponent;
  public saving = false;
  public saveError = "";
  public saveStatus = "";
  private readonly updatedDates = new Map<string, CompletionDatesDto>();
  protected subscriptions: Subscription[] = [];

  constructor(
    protected translate: TranslateService,
    protected worksService: WorksService,
    private readonly toastService: ToastService,
    protected cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.setSubmitText("SAVE");

    const translateSubscription = this.translate.onLangChange.subscribe(() => {
      this.setSubmitText("SAVE");
      this.cdr.markForCheck();
    });
    this.subscriptions.push(translateSubscription);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.["item"]) {
      this.cdr.markForCheck();
    }
  }

  private setSubmitText(key: string): void {
    const translateSubscription = this.translate
      .get(key)
      .subscribe((translatedText) => {
        this.submitText = translatedText;
        this.cdr.markForCheck();
      });
    this.subscriptions.push(translateSubscription);
  }

  public handleDateChange(event: CompletionDatesDto) {
    this.saveError = "";
    this.saveStatus = "";
    const key = `${
      (event as SeriesCompletionDateDto).season ??
      (event as VolumeCompletionDateDto).volume ??
      (event as GameCompletionDateDto).completion ??
      (event as BookCompletionDateDto).completion
    }-${(event as SeriesCompletionDateDto).episode ?? ""}`;
    this.updatedDates.set(key, event);
  }

  public saveAllDates() {
    if (this.pending || this.timeHistory?.loading || !this.user) return;
    this.saveError = "";
    this.saveStatus = "";
    if (this.timeHistory?.editing && !this.timeHistory.deleting) {
      this.timeHistory.save(() => this.saveCompletionDates(true));
      return;
    }
    this.saveCompletionDates();
  }

  public get pending(): boolean {
    return this.saving || !!this.timeHistory?.pending;
  }

  private saveCompletionDates(sessionSaved = false): void {
    if (this.timeHistory && !this.updatedDates.size) {
      this.saveStatus = sessionSaved
        ? "TIME_TRACKING.SESSION_UPDATED"
        : "TIME_TRACKING.NO_CHANGES";
      if (sessionSaved) this.timeHistory.load();
      this.cdr.markForCheck();
      return;
    }
    this.saving = true;
    this.setSubmitText("SAVING");

    const completionDates: CompletionDatesDto[] = Array.from(
      this.updatedDates.values()
    );

    if (this.user) {
      const updateAllWorksSubscription = this.worksService
        .updateWorkCompletionDatesByType(
          this.user._id,
          this.item._id,
          this.item.type,
          completionDates
        )
        .pipe(
          finalize(() => {
            this.saving = false;
            this.setSubmitText("SAVE");
            this.cdr.markForCheck();
          })
        )
        .subscribe({
          next: (saved) => {
            if (!saved) {
              this.onSaveError(sessionSaved);
              return;
            }
            for (const [key, date] of this.updatedDates) {
              if (completionDates.includes(date)) this.updatedDates.delete(key);
            }
            this.timeHistory?.load();
            this.saveStatus = "WORK_UPDATED";
            if (this.timeHistory) {
              this.saveStatus = sessionSaved
                ? "TIME_TRACKING.SESSION_AND_DATES_UPDATED"
                : "TIME_TRACKING.COMPLETION_DATES_UPDATED";
            }
            this.toastService.sendToast({
              text: this.saveStatus,
              classes: "bg-form-success",
            });
            this.cdr.markForCheck();
          },
          error: () => {
            this.onSaveError(sessionSaved);
          },
        });

      this.subscriptions.push(updateAllWorksSubscription);
    }
  }

  private onSaveError(sessionSaved: boolean): void {
    if (sessionSaved) this.timeHistory?.load();
    this.saveError = sessionSaved
      ? "TIME_TRACKING.SESSION_SAVED_DATES_ERROR"
      : "ERROR_UPDATE";
    this.toastService.sendToast({
      text: this.saveError,
      classes: "bg-form-error",
    });
    this.cdr.markForCheck();
  }

  ngOnDestroy() {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
  }
}
